import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader, PageHeaderWithBack } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { formsService } from "@/api/services/forms.service";
import { FormDisplay } from "./components/FormDisplay";
import styles from "./FormDetail.module.css";
const getDefaultFormData = () => ({
    formType: "Building Permit Form",
    recipient: "",
    applicantRole: "",
    applicantAcnArbn: "",
    contactName: "",
    email: "",
    companyName: "",
    phone: "",
    address: "",
    servingAddress: "",
    applicantDetails: "",
    isLeaseeOrLicensee: false,
    propertyDetails: {
        buildingName: "",
        shopNo: "",
        fullAddress: "",
        lotNumber: "",
        lpPs: "",
        vol: "",
        fol: "",
        crownAllot: "",
        section: "",
        parish: "",
        county: "",
        council: "",
        landOwnedByCrownOrPublicAuthority: false,
    },
    buildingOwner: {
        name: "",
        contactPerson: "",
        email: "",
        company: "",
        phone: "",
        address: "",
        acn: "",
        acnArbn: "",
    },
    tenant: {
        contactPerson: "",
        email: "",
        company: "",
        phone: "",
        address: "",
    },
    builder: {
        name: "",
        contactPerson: "",
        email: "",
        company: "",
        phone: "",
        address: "",
        practitionerNo: "",
        acnArbn: "",
        postalAddress: "",
        naturalPerson: {
            contactPerson: "",
            email: "",
            address: "",
            phone: "",
        },
    },
    lesseeResponsible: false,
    buildingPractitioners: [
        { name: "", categoryOrClass: "", registrationNo: "" },
        { name: "", categoryOrClass: "", registrationNo: "" },
        { name: "", categoryOrClass: "", registrationNo: "" },
    ],
    ownerBuilderSection: {
        intendsOwnerBuilder: false,
        ownerBuilderCertificateOfConsentNo: "",
        signedByOwner: "",
    },
    natureOfBuildingWork: {
        constructionNewBuilding: false,
        demolitionBuilding: false,
        reErectionBuilding: false,
        alterationsExistingBuilding: false,
        removalBuilding: false,
        extensionBuilding: false,
        tenancyFitout: false,
        changeOfUse: false,
        otherDescriptionEnabled: false,
        otherDescription: "",
        constructionSwimmingPoolOrSpa: false,
        constructionSwimmingPoolOrSpaBarrier: false,
        descriptionOfProposedBuildingWorks: "",
        descriptionOfUseOfBuilding: "",
    },
    buildingDetails: {
        proposedUseOfBuilding: "",
        proposedClassOfBuilding: "",
        currentUseOfBuilding: "",
        currentClassOfBuilding: "",
        includesSocialHousing: false,
        isEmergencyRecoveryDwelling: false,
    },
    costOfBuildingWork: {
        nonOwnerBuilder: {
            hasLumpSumContract: false,
            lumpSumContractPrice: "",
            hasCostPlusContract: false,
            costPlusEstimatedTotal: "",
            chattelsCost: "",
            prescribedExcludedItemsCost: "",
            relatesToMixedClasses: false,
            isStageOfWholeWork: false,
            stageExtentDescription: "",
            hasWholeLumpSumContract: false,
            wholeLumpSumContractPrice: "",
            hasWholeCostPlusContract: false,
            wholeCostPlusEstimatedTotal: "",
            wholeChattelsCost: "",
            wholePrescribedExcludedItemsCost: "",
        },
        ownerBuilder: {
            isOwnerBuilder: false,
            ownerBuilderCertificateOfConsentNo: "",
            ownerBuilderEstimatedCost: "",
            ownerEngagesBuilders: false,
            engagedBuildersTotalAmount: "",
            engagedBuilders: [{ amount: "" }, { amount: "" }, { amount: "" }],
            ownerChattelsCost: "",
            ownerPrescribedExcludedItemsCost: "",
            ownerIsStageOfWholeWork: false,
            ownerStageExtentDescription: "",
            ownerWholeEstimatedCost: "",
            ownerWholeChattelsCost: "",
            ownerWholePrescribedExcludedItemsCost: "",
            ownerHasLumpSumBuilderForStages: false,
            ownerLumpSumStagesPrice: "",
            ownerHasCostPlusBuilderForStages: false,
            ownerCostPlusStagesEstimate: "",
            ownerStagesChattelsCost: "",
            ownerStagesExcludedItemsCost: "",
            ownerRelatesToMixedClasses: false,
        },
    },
    signatureDeclaration: {
        applicantSignature: "",
        printName: "",
        signatureDate: "",
    },
});
export const mergeFormData = (payload) => {
    const base = getDefaultFormData();
    return {
        ...base,
        ...payload,
        propertyDetails: {
            ...base.propertyDetails,
            ...payload.propertyDetails,
        },
        buildingOwner: {
            ...base.buildingOwner,
            ...payload.buildingOwner,
            acnArbn: payload.buildingOwner?.acnArbn ??
                payload.buildingOwner?.acn ??
                base.buildingOwner?.acnArbn,
        },
        tenant: {
            ...base.tenant,
            ...payload.tenant,
        },
        builder: {
            ...base.builder,
            ...payload.builder,
            naturalPerson: {
                ...base.builder?.naturalPerson,
                ...(payload.builder?.naturalPerson ?? {}),
            },
        },
        buildingPractitioners: payload.buildingPractitioners ?? base.buildingPractitioners,
        ownerBuilderSection: {
            ...base.ownerBuilderSection,
            ...payload.ownerBuilderSection,
        },
        natureOfBuildingWork: {
            ...base.natureOfBuildingWork,
            ...payload.natureOfBuildingWork,
        },
        buildingDetails: {
            ...base.buildingDetails,
            ...payload.buildingDetails,
        },
        costOfBuildingWork: {
            ...base.costOfBuildingWork,
            ...payload.costOfBuildingWork,
            nonOwnerBuilder: {
                ...(base.costOfBuildingWork?.nonOwnerBuilder ?? {}),
                ...(payload.costOfBuildingWork?.nonOwnerBuilder ?? {}),
            },
            ownerBuilder: {
                ...(base.costOfBuildingWork?.ownerBuilder ?? {}),
                ...(payload.costOfBuildingWork?.ownerBuilder ?? {}),
                engagedBuilders: payload.costOfBuildingWork?.ownerBuilder?.engagedBuilders ??
                    base.costOfBuildingWork?.ownerBuilder?.engagedBuilders,
            },
        },
        signatureDeclaration: {
            ...base.signatureDeclaration,
            ...payload.signatureDeclaration,
        },
    };
};
export { getDefaultFormData };
export const FormDetail = () => {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState(null);
    const [formData, setFormData] = useState(getDefaultFormData());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        const fetchForm = async () => {
            if (!formId) {
                setIsLoading(false);
                return;
            }
            try {
                setIsLoading(true);
                setError(null);
                const response = await formsService.getForm(formId);
                setForm(response.data);
                setFormData(mergeFormData((response.data.data ?? {})));
            }
            catch (err) {
                console.error("Failed to fetch form from API:", err);
                setError("Failed to load form. Please try again.");
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchForm();
    }, [formId]);
    const handleEdit = () => {
        navigate(`/admin/forms/${formId}/edit`);
    };
    if (isLoading) {
        return (_jsxs("div", { className: "page-container", children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Form Details", onBack: () => navigate(-1) }), _jsx("div", { className: "page-main-content", children: _jsx("div", { style: { padding: "40px", textAlign: "center" }, children: "Loading form..." }) })] }));
    }
    if (error) {
        return (_jsxs("div", { className: "page-container", children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Form Details", onBack: () => navigate(-1) }), _jsx("div", { className: "page-main-content", children: _jsx("div", { style: { padding: "40px", textAlign: "center", color: "red" }, children: error }) })] }));
    }
    return (_jsxs("div", { className: "page-container", children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Form Details", onBack: () => navigate(-1) }), _jsxs("div", { className: "page-main-content", children: [_jsx(FormDisplay, { form: form, formData: formData }), _jsx("div", { className: styles.bottomActions, children: _jsx("div", { className: styles.actionButtons, children: _jsx("button", { className: styles.btnPrimary, onClick: handleEdit, children: "Edit" }) }) })] })] }));
};
